<template>
  <div>
    <v-layout class="pull-left">
      <v-dialog :modelValue="show" max-width="1200px" @keydown.esc="closeModal()" @click:outside="closeModal()">
        <v-card>
          <v-card-title class="headline">
            <span>Detalle de tarea #{{ task != null ? task.id : "" }}</span>
          </v-card-title>

          <v-card-text v-if="task != null && task.id > 0">
            <TaskViewHeader :task="task" />

            <v-divider class="my-4"></v-divider>

            <component :is="detalle_component" :task="task" :descriptivos="descriptivos" />

            <v-divider class="my-4"></v-divider>

            <h6 class="mb-2">Movimientos ejecutados</h6>
            <v-alert v-if="!tiene_ejecucion" type="info" variant="tonal" density="compact">
              La tarea todavía no fue ejecutada, no hay movimientos registrados.
            </v-alert>
            <v-alert v-else-if="history_error" type="error" variant="tonal" density="compact">
              No se pudo cargar el historial de movimientos: {{ history_error }}
            </v-alert>

            <v-data-table v-else class="mt-2" :headers="headers" :items="history" :loading="loading_history" loading-text="Cargando..." no-data-text="Sin movimientos registrados">
              <template v-slot:item="{ item, index }">
                <tr>
                  <td>
                    <v-btn variant="text" size="small" @click="toggleExpand(index)" icon>
                      <v-icon>{{ isExpanded(index) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
                    </v-btn>
                  </td>

                  <!-- Creación -->
                  <td>
                    {{ item.date_time.split(" ")[0] }}<br />
                    <span class="text-caption text-grey-600">{{ item.date_time.split(" ")[1] || "" }}</span
                    ><br />
                    <span class="text-caption text-grey-600">{{ item.user_username || "-" }}</span>
                  </td>

                  <!-- Producto -->
                  <td>
                    <div v-if="item.destination_pa_id" class="d-flex flex-column">
                      <span class="font-weight-medium">{{ item.destination_product_name }}</span>
                      <span class="text-caption text-grey-600 nowrap">SKU: {{ item.destination_product_sku }}</span>
                      <v-chip
                        class="fit-content"
                        size="small"
                        v-if="item.destination_product_status_id"
                        :color="getStatusColor(item.destination_product_status_id)"
                      >
                        <b>{{ item.destination_product_status_name }}</b>
                      </v-chip>
                    </div>
                    <div v-else-if="item.origin_pa_id" class="d-flex flex-column">
                      <span class="font-weight-medium">{{ item.origin_product_name }}</span>
                      <span class="text-caption text-grey-600 nowrap">SKU: {{ item.origin_product_sku }}</span>
                      <v-chip class="fit-content" size="small" v-if="item.origin_product_status_id" :color="getStatusColor(item.origin_product_status_id)">
                        <b>{{ item.origin_product_status_name }}</b>
                      </v-chip>
                    </div>
                    <span v-else>-</span>
                  </td>

                  <!-- Cantidad -->
                  <td class="text-center pt-2 pb-2">
                    <div class="d-flex flex-column align-center justify-center">
                      <div v-if="item.destination_pa_id" class="d-flex align-center font-weight-bold">
                        <div class="text-stock mr-0">
                          {{ item.stock }}
                        </div>
                        <div class="text-caption text-grey-600 flex-shrink-0 mt-1 presentation-type">
                          {{
                            getPresentationTypeName(
                              item.stock,
                              item.destination_presentation_type_id,
                              item.destination_presentation_type_name
                            )
                          }}
                        </div>
                      </div>
                      <div v-else-if="item.origin_pa_id" class="d-flex align-center font-weight-bold">
                        <div class="text-stock mr-0">
                          {{ item.stock }}
                        </div>
                        <div class="text-caption text-grey-600 flex-shrink-0 mt-1 presentation-type">
                          {{
                            getPresentationTypeName(
                              item.stock,
                              item.origin_presentation_type_id,
                              item.origin_presentation_type_name
                            )
                          }}
                        </div>
                      </div>
                      <span v-else>-</span>
                    </div>
                  </td>

                  <!-- LPN Origen/Destino -->
                  <td>
                    <div class="d-flex align-center gap-3 mt-1 mb-1 mr-0 mt-2 mb-2">
                      <div v-if="item.origin_lpn_code || item.origin_ubicacion_code || item.origin_lpn_status" class="d-flex flex-column">
                        <span class="font-weight-bold text-caption">Origen</span>
                        <span v-if="item.origin_lpn_code">{{ item.origin_lpn_code }}</span>
                        <span v-else>&nbsp;</span>
                        <span v-if="item.origin_ubicacion_code" class="text-caption text-grey-600 nowrap">{{ item.origin_ubicacion_code }}</span>
                        <span v-else>-</span>
                        <v-chip v-if="item.origin_lpn_code && item.origin_lpn_status" size="small" class="mt-0 chip-auto-width">{{ item.origin_lpn_status_name }}</v-chip>
                        <span v-else>&nbsp;</span>
                      </div>

                      <div
                        v-if="(item.origin_lpn_code || item.origin_ubicacion_code || item.origin_lpn_status) && (item.destination_lpn_code || item.destination_ubicacion_code || item.destination_lpn_status)"
                        class="vertical-divider"
                      ></div>

                      <div v-if="item.destination_lpn_code || item.destination_ubicacion_code || item.destination_lpn_status" class="d-flex flex-column ml-0">
                        <span class="font-weight-bold text-caption">Destino</span>
                        <span v-if="item.destination_lpn_code">{{ item.destination_lpn_code }}</span>
                        <span v-else>&nbsp;</span>
                        <span v-if="item.destination_ubicacion_code" class="text-caption text-grey-600 nowrap">{{ item.destination_ubicacion_code }}</span>
                        <span v-else>-</span>
                        <v-chip v-if="item.destination_lpn_code && item.destination_lpn_status" size="small" class="mt-0 chip-auto-width">{{ item.destination_lpn_status_name }}</v-chip>
                        <span v-else>&nbsp;</span>
                      </div>
                    </div>
                  </td>

                  <!-- Documento -->
                  <td>
                    <div class="d-flex flex-column gap-1">
                      <div v-if="item.asn_id">
                        <v-chip size="small" color="blue">ASN #{{ item.asn_id }}</v-chip>
                      </div>
                      <div v-if="item.shipment_order_id">
                        <v-chip size="small" color="green">Envío #{{ item.shipment_order_code }}</v-chip>
                      </div>
                      <div v-if="item.task_id">
                        <v-chip size="small" color="orange">
                          Tarea #{{ item.task_id }}
                          <template v-if="item.task_type_id"> ({{ item.task_type_name }}) </template>
                        </v-chip>
                      </div>
                      <div v-if="item.historial_ajuste_id">
                        <v-chip size="small" color="purple">Ajuste #{{ item.historial_ajuste_id }}</v-chip>
                      </div>
                    </div>
                  </td>

                  <!-- Observación -->
                  <td>
                    <span class="text-caption">{{ item.observations || "-" }}</span>
                  </td>
                </tr>

                <!-- Fila expandida con detalles adicionales -->
                <tr v-if="isExpanded(index)" :key="`expanded-${index}`">
                  <td :colspan="headers.length" class="pa-0">
                    <div class="pa-4 bg-grey-lighten-5">
                      <h6 class="mb-3 ml-4">Detalles de Atributos</h6>
                      <v-row class="ml-1">
                        <!-- Atributos de Destino -->
                        <v-col cols="12" md="6" v-if="item.destination_pa_id">
                          <v-row>
                            <v-col cols="12" md="6" v-if="item.destination_pa_lote">
                              <span class="font-weight-bold pb-1 text-caption">Lote</span>
                              <div>{{ item.destination_pa_lote }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="item.destination_pa_serie">
                              <span class="font-weight-bold pb-1 text-caption">Serie</span>
                              <div>{{ item.destination_pa_serie }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="item.destination_pa_storage_units_amount">
                              <span class="font-weight-bold pb-1 text-caption">Almacenamiento</span>
                              <div>{{ item.destination_pa_storage_units_amount }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="item.destination_quality_id">
                              <span class="font-weight-bold pb-1 text-caption">Calidad</span>
                              <div>
                                <v-chip size="small" color="primary">{{ item.destination_quality_name }}</v-chip>
                              </div>
                            </v-col>
                            <v-col cols="12" md="3" v-if="item.destination_pa_expiration_date">
                              <span class="font-weight-bold pb-1 text-caption">Vencimiento</span>
                              <div>{{ item.destination_pa_expiration_date }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="isAliasEnabled('descriptivo_1') && item.destination_pa_descriptivo_1">
                              <span class="font-weight-bold pb-1 text-caption">{{ getAliasName("descriptivo_1") }}</span>
                              <div>{{ item.destination_pa_descriptivo_1 }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="isAliasEnabled('descriptivo_2') && item.destination_pa_descriptivo_2">
                              <span class="font-weight-bold pb-1 text-caption">{{ getAliasName("descriptivo_2") }}</span>
                              <div>{{ item.destination_pa_descriptivo_2 }}</div>
                            </v-col>
                            <v-col cols="12" md="6" v-if="isAliasEnabled('descriptivo_3') && item.destination_pa_descriptivo_3">
                              <span class="font-weight-bold pb-1 text-caption">{{ getAliasName("descriptivo_3") }}</span>
                              <div>{{ item.destination_pa_descriptivo_3 }}</div>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="closeModal()">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>

<script>
import { taskService } from "@/libs/ws/taskService";
import { lpnService } from "@/libs/ws/lpnService";
import { productoService } from "@/libs/ws/productoService";
import AliasMixin from "@/mixins/AliasMixin.vue";
import GenericMixin from "@/mixins/GenericMixin.vue";
import TaskViewHeader from "./TaskViewHeader.vue";
import TaskViewDetalleTransferencia from "./TaskViewDetalleTransferencia.vue";
import TaskViewDetalleGuardado from "./TaskViewDetalleGuardado.vue";
import TaskViewDetallePreparacion from "./TaskViewDetallePreparacion.vue";
import TaskViewDetalleConteo from "./TaskViewDetalleConteo.vue";
import TaskViewDetalleGenerico from "./TaskViewDetalleGenerico.vue";

export default {
  props: {
    show: Boolean,
    task: Object,
  },
  name: "ModalView",
  components: {
    TaskViewHeader,
    TaskViewDetalleTransferencia,
    TaskViewDetalleGuardado,
    TaskViewDetallePreparacion,
    TaskViewDetalleConteo,
    TaskViewDetalleGenerico,
  },
  mixins: [AliasMixin, GenericMixin],
  created() {},
  data() {
    return {
      taskService: taskService,
      loading_history: false,
      history: [],
      history_error: null,
      headers: [],
      expandedItems: [],
      allExpanded: false,
    };
  },
  mounted() {
    this.initializeHeaders();
  },
  computed: {
    task_type_id() {
      return this.task && this.task.task_type ? this.task.task_type.id : null;
    },
    detalle_component() {
      switch (this.task_type_id) {
        case taskService.TASK_TRANSFERENCIA:
        case taskService.TASK_REPOSICION:
          return "TaskViewDetalleTransferencia";
        case taskService.TASK_GUARDADO:
          return "TaskViewDetalleGuardado";
        case taskService.TASK_PREPARACION:
          return "TaskViewDetallePreparacion";
        case taskService.TASK_CONTROL:
          return "TaskViewDetalleConteo";
        default:
          return "TaskViewDetalleGenerico";
      }
    },
    tiene_ejecucion() {
      const s = this.task && this.task.state ? this.task.state.id : null;
      return [taskService.STATE_EN_PROCESO, taskService.STATE_FINALIZADO, taskService.STATE_CANCELADA].includes(s);
    },
    descriptivos() {
      return [1, 2, 3]
        .map((i) => "descriptivo_" + i)
        .filter((key) => this.isAliasEnabled(key))
        .map((key) => ({ key: key, label: this.getAliasName(key) }));
    },
  },
  watch: {
    // "task" (no "show"): "show" es prop de este componente Y data de GenericMixin (mixins: [AliasMixin,
    // GenericMixin]) con el mismo nombre - esa colision hace que un watcher sobre "show" nunca se dispare
    // (mismo patron que rompia this.close() en el ESC global, ver metodo close() de GenericMixin).
    // "task" no colisiona con nada del mixin, y getHistory() ya resetea todo su propio estado al entrar.
    task() {
      this.getHistory();
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    getHistory() {
      this.history = [];
      this.history_error = null;
      this.expandedItems = [];

      // Las tareas Pendientes/Asignadas todavia no pudieron generar movimientos: no se consulta al backend
      if (this.task == null || !(this.task.id > 0) || !this.tiene_ejecucion) return;

      this.loading_history = true;

      const param = {
        task: { id: this.task.id },
      };

      // eslint-disable-next-line no-console
      console.log("[ModalView] getHistory param =", JSON.parse(JSON.stringify(param)));

      lpnService
        .getHistory(param)
        .then((response) => {
          // eslint-disable-next-line no-console
          console.log("[ModalView] getHistory response =", response);

          // wsService devuelve {status:false, msg, ...} SIN "result" ante cualquier error de red/HTTP,
          // en vez de rechazar la promesa. Se distingue explicitamente para no mostrar "sin movimientos"
          // cuando en realidad la request fallo.
          if (!response || response.status !== true || response.result == null) {
            this.history_error = (response && response.msg) || "Respuesta inesperada del servidor.";
            console.error("lpn_labels/history: respuesta con error", response);
            return;
          }
          this.history = response.result.list;
        })
        .catch((error) => {
          this.history_error = "Error de conexión.";
          console.error("lpn_labels/history: excepción", error);
          this.toast.error(error);
        })
        .finally(() => {
          this.loading_history = false;
        });
    },

    initializeHeaders() {
      this.headers = [
        { title: "", key: "data-table-expand", sortable: false, width: "48px" },
        {
          title: "Creación",
          align: "left",
          sortable: true,
          key: "date_time",
        },
        {
          title: "Producto",
          align: "left",
          sortable: false,
          key: "product",
        },
        {
          title: "Cantidad",
          align: "center",
          sortable: false,
          key: "quantity",
          width: "100px",
        },
        {
          title: "LPN Origen/Destino",
          align: "left",
          sortable: false,
          key: "lpn",
        },
        {
          title: "Documento",
          align: "left",
          sortable: false,
          key: "document",
        },
        {
          title: "Observación",
          align: "left",
          sortable: false,
          key: "observations",
        },
      ];

      // Filtrar headers según tipo de usuario
      this.headers = this.headers.filter((header) => {
        if (header.visible_for_client || header.visible_for_client == undefined) {
          return true;
        }
        return !this.isClient();
      });
    },

    // Funciones para manejo de expansión
    toggleExpand(index) {
      const i = this.expandedItems.indexOf(index);
      if (i > -1) {
        this.expandedItems.splice(i, 1);
      } else {
        this.expandedItems.push(index);
      }
    },

    isExpanded(index) {
      return this.expandedItems.includes(index);
    },

    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedItems = [];
      } else {
        this.expandedItems = this.history.map((_, index) => index);
      }
      this.allExpanded = !this.allExpanded;
    },

    getStatusColor(status) {
      return productoService.getStatusColor(status);
    },
  },
};
</script>

<style scoped>
.fit-content {
  width: fit-content !important;
  min-width: auto !important;
}

.nowrap {
  white-space: nowrap;
}

.gap-1 > * + * {
  margin-top: 4px;
}

.gap-3 > * + * {
  margin-left: 12px;
}

/* Mejorar el espaciado horizontal */
.d-flex.align-center.gap-3 {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.d-flex.align-center.gap-3 > .d-flex.flex-column {
  flex: 1;
  min-width: 0;
}

/* Chip con ancho automático */
.chip-auto-width {
  width: fit-content !important;
  min-width: auto !important;
}

/* Divisor vertical */
.vertical-divider {
  width: 1px;
  height: 40px;
  background-color: #e0e0e0;
  margin: 0 8px;
}

/* Estilos para stock con tipo de presentación */
.text-stock {
  font-size: 1.1rem;
  font-weight: bold;
  margin-right: 4px;
}

.presentation-type {
  margin-left: 4px;
  font-size: 0.75rem;
  color: #666;
}
</style>
